import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Chip, Stack, Alert, Paper, Divider,
  Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment, Accordion, AccordionSummary,
  AccordionDetails, Switch, FormControlLabel
} from '@mui/material';
import {
  PhoneAndroid, Api, People, School, Send, Notifications,
  ExpandMore, ContentCopy, Check, Refresh, Delete, BarChart,
  Lock, Smartphone, Tablet, CheckCircle, Warning, Code
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={copy}>{copied ? <Check fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}</IconButton>
    </Tooltip>
  );
}

function EndpointCard({ method, path, desc, auth, params, response, example }) {
  const methodColor = { GET:'#2e7d32', POST:'#1565c0', PUT:'#e65100', DELETE:'#c62828' };
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip label={method} size="small" sx={{ bgcolor: methodColor[method], color: 'white', fontWeight: 700, fontFamily: 'monospace', minWidth: 52 }} />
          <Typography fontFamily="monospace" fontSize={13}>{path}</Typography>
          <Typography fontSize={12} color="text.secondary">{desc}</Typography>
          {auth && <Chip label={auth} size="small" color={auth === 'parent' ? 'secondary' : auth === 'teacher' ? 'primary' : 'default'} sx={{ fontSize: 10 }} />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ bgcolor: '#fafafa', borderTop: '1px solid #eee' }}>
        <Grid container spacing={2}>
          {params && (
            <Grid item xs={12} md={6}>
              <Typography fontSize={12} fontWeight={700} gutterBottom>Parameters</Typography>
              {params.map(p => (
                <Stack key={p.name} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Chip label={p.name} size="small" sx={{ fontFamily: 'monospace', fontSize: 10 }} />
                  <Chip label={p.type} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                  {p.required && <Chip label="required" size="small" color="error" sx={{ fontSize: 10 }} />}
                  <Typography fontSize={11} color="text.secondary">{p.desc}</Typography>
                </Stack>
              ))}
            </Grid>
          )}
          {example && (
            <Grid item xs={12} md={6}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontSize={12} fontWeight={700} gutterBottom>Example Response</Typography>
                <CopyButton text={JSON.stringify(example, null, 2)} />
              </Stack>
              <Box sx={{ bgcolor: '#1e1e1e', color: '#d4d4d4', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: 11, overflowX: 'auto', maxHeight: 200, overflowY: 'auto' }}>
                <pre style={{ margin: 0 }}>{JSON.stringify(example, null, 2)}</pre>
              </Box>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

// ── API Docs Tab ───────────────────────────────────────────────────────────────
function DocsTab() {
  const base = `${API_BASE}/mobile/v1`;

  const PARENT_ENDPOINTS = [
    { method:'POST', path:'/device/register', desc:'Register push notification token', auth:'parent/teacher',
      params:[{name:'device_token',type:'string',required:true,desc:'FCM or APNs device token'},{name:'platform',type:'ios|android',desc:'Device platform'},{name:'app_version',type:'string',desc:'App version'}],
      example:{ success:true, data:{ registered:true } }
    },
    { method:'GET',  path:'/notifications', desc:'Get push notifications (paginated)', auth:'parent/teacher',
      params:[{name:'page',type:'number',desc:'Page number'},{name:'limit',type:'number',desc:'Items per page (max 50)'}],
      example:{ success:true, data:[{id:1,title:'Attendance Alert',body:'Your child was absent today',type:'attendance',is_read:false,sent_at:'2025-01-15T08:00:00Z'}], meta:{ unread:3, total:12 } }
    },
    { method:'GET',  path:'/parent/home', desc:'Home screen — all children with summary stats', auth:'parent',
      example:{ success:true, data:[{id:1, first_name:'John', last_name:'Doe', admission_number:'STU001', class_name:'Grade 8 A', today_status:'present', pending_homework:2, outstanding_fees:350.00, unread_notifications:1}] }
    },
    { method:'GET',  path:'/parent/child/attendance', desc:"Child's attendance records", auth:'parent',
      params:[{name:'student_id',type:'number',required:true,desc:'Student ID'},{name:'date_from',type:'date',desc:'YYYY-MM-DD'},{name:'date_to',type:'date',desc:'YYYY-MM-DD'},{name:'page',type:'number',desc:'Page'}],
      example:{ success:true, data:[{date:'2025-01-15',status:'present',notes:null}], meta:{ summary:{ total_days:80, present:72, absent:5, late:3 } } }
    },
    { method:'GET',  path:'/parent/child/marks', desc:'Child marks grouped by subject', auth:'parent',
      params:[{name:'student_id',type:'number',required:true},{name:'academic_year_id',type:'number'}],
      example:{ success:true, data:[{subject:'Mathematics', code:'MATH', avg:68.5, assessments:[{title:'Mid-Term',score:72,max_score:100,percentage:72,grade:'B',date:'2025-01-10'}]}] }
    },
    { method:'GET',  path:'/parent/child/fees', desc:'Fee invoices and payment status', auth:'parent',
      params:[{name:'student_id',type:'number',required:true}],
      example:{ success:true, data:[{invoice_number:'INV-2025-0001',description:'Term 1 Tuition',amount:1200,amount_paid:600,balance:600,status:'partial',due_date:'2025-02-01'}], meta:{ summary:{ outstanding:600 } } }
    },
    { method:'GET',  path:'/parent/child/homework', desc:'Homework assignments and submission status', auth:'parent',
      params:[{name:'student_id',type:'number',required:true},{name:'status',type:'pending|submitted|missing'}],
      example:{ success:true, data:[{title:'Chapter 5 exercises',subject:'Mathematics',due_date:'2025-01-20',priority:'normal',submission_status:'pending',teacher_name:'Ms. Dlamini'}], meta:{ summary:{ pending:3, missing:1 } } }
    },
    { method:'GET',  path:'/parent/child/disciplinary', desc:'Disciplinary incidents (parent-visible only)', auth:'parent',
      params:[{name:'student_id',type:'number',required:true}],
      example:{ success:true, data:[{incident_date:'2025-01-10',incident_type:'late_arrival',description:'Arrived 20 minutes late',action_taken:'Verbal warning',resolved:0}] }
    },
  ];

  const TEACHER_ENDPOINTS = [
    { method:'GET',  path:'/teacher/home', desc:'Home screen — today stats, pending tasks', auth:'teacher',
      example:{ success:true, data:{ teacher:{first_name:'Sarah',last_name:'Dlamini'}, academic_year:{year_label:'2025',term:'Term 1'}, stats:{ hw_pending:5, att_taken:2, pb_overdue:1, class_count:4 }, classes:[{id:1,name:'Grade 8',stream:'A',subject_name:'Mathematics',student_count:32}] } }
    },
    { method:'GET',  path:'/teacher/classes', desc:'All class-subject assignments', auth:'teacher',
      example:{ success:true, data:[{id:1,name:'Grade 8',stream:'A',subject_id:3,subject_name:'Mathematics',student_count:32}] }
    },
    { method:'GET',  path:'/teacher/classes/:class_id/students', desc:'Students in a class with today\'s attendance', auth:'teacher',
      example:{ success:true, data:[{student_id:1,first_name:'John',last_name:'Doe',admission_number:'STU001',gender:'male',today_attendance:'present'}] }
    },
    { method:'GET',  path:'/teacher/attendance', desc:'Attendance register for a class on a date', auth:'teacher',
      params:[{name:'class_id',type:'number',required:true},{name:'date',type:'date',desc:'Default: today'}],
      example:{ success:true, data:[{student_id:1,first_name:'John',att_id:5,status:'present',notes:null}], meta:{ date:'2025-01-15', taken:true } }
    },
    { method:'POST', path:'/teacher/attendance', desc:'Save attendance (bulk, offline-safe upsert)', auth:'teacher',
      params:[{name:'class_id',type:'number',required:true},{name:'date',type:'date'},{name:'entries',type:'array',required:true,desc:'[{student_id, status, notes}]'}],
      example:{ success:true, data:{ saved:32, date:'2025-01-15' } }
    },
    { method:'GET',  path:'/teacher/marks', desc:'Marks for an assessment', auth:'teacher',
      params:[{name:'assessment_id',type:'number',required:true},{name:'class_id',type:'number',required:true}],
      example:{ success:true, data:[{student_id:1,first_name:'John',mark_id:null,score:null}] }
    },
    { method:'POST', path:'/teacher/marks', desc:'Save marks (bulk upsert — offline-safe)', auth:'teacher',
      params:[{name:'assessment_id',type:'number',required:true},{name:'entries',type:'array',required:true,desc:'[{student_id, score}]'}],
      example:{ success:true, data:{ saved:30 } }
    },
    { method:'GET',  path:'/teacher/homework', desc:'My homework assignments', auth:'teacher',
      example:{ success:true, data:[{id:1,title:'Chapter 5',due_date:'2025-01-20',priority:'normal',submitted:15,missing:3,pending:14}] }
    },
    { method:'GET',  path:'/teacher/prep-book', desc:'Prep book entries', auth:'teacher',
      params:[{name:'class_id',type:'number'},{name:'subject_id',type:'number'},{name:'week_number',type:'number'},{name:'status',type:'planned|covered|postponed'}],
      example:{ success:true, data:[{id:1,week_number:3,lesson_number:1,planned_date:'2025-01-15',topic:'Quadratic Equations',status:'planned',syllabus_ref:'4.2.1'}], meta:{ progress:{ total:40,covered:28,overdue:2 } } }
    },
    { method:'PUT',  path:'/teacher/prep-book/:id/cover', desc:'Mark lesson as covered', auth:'teacher',
      params:[{name:'coverage_notes',type:'string'},{name:'actual_date',type:'date'}],
      example:{ success:true, data:{ covered:true } }
    },
  ];

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3, fontSize: 12 }}>
        All mobile endpoints are at <strong>{base}</strong>. Authentication uses the same JWT tokens as the web app — pass in the <code>Authorization: Bearer &lt;token&gt;</code> header. All responses follow the envelope: <code>{'{ success, version, meta, data }'}</code>. Parent tokens have <code>role: "parent"</code>, teacher/staff tokens use <code>role: "teacher"</code> or the user's role.
      </Alert>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Chip label={`Base URL: ${base}`} sx={{ fontFamily: 'monospace', fontSize: 11 }} />
        <CopyButton text={base} />
      </Stack>

      {/* Auth info */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, p: 2 }}>
        <Typography fontWeight={700} gutterBottom>Authentication</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography fontSize={12} fontWeight={600} gutterBottom>Parent Login</Typography>
            <Box sx={{ bgcolor: '#1e1e1e', color: '#d4d4d4', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: 11 }}>
              <pre style={{ margin: 0 }}>{`POST /api/parent/login
Body: { email, password }
→ { token, user }`}</pre>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography fontSize={12} fontWeight={600} gutterBottom>Teacher / Staff Login</Typography>
            <Box sx={{ bgcolor: '#1e1e1e', color: '#d4d4d4', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: 11 }}>
              <pre style={{ margin: 0 }}>{`POST /api/auth/login
Body: { username, password }
→ { token, user, permissions }`}</pre>
            </Box>
          </Grid>
        </Grid>
      </Card>

      <Typography fontWeight={700} fontSize={15} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <People color="secondary" /> Parent App Endpoints
      </Typography>
      <Stack sx={{ mb: 3 }}>
        {PARENT_ENDPOINTS.map((e, i) => <EndpointCard key={i} {...e} />)}
      </Stack>

      <Typography fontWeight={700} fontSize={15} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <School color="primary" /> Teacher App Endpoints
      </Typography>
      <Stack>
        {TEACHER_ENDPOINTS.map((e, i) => <EndpointCard key={i} {...e} />)}
      </Stack>
    </Box>
  );
}

// ── Push Notifications Tab ─────────────────────────────────────────────────────
function NotificationsTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm]   = useState({ user_type:'parent', user_id:'', title:'', body:'', type:'info' });
  const [bcastForm, setBcast] = useState({ student_id:'', title:'', body:'', type:'attendance' });
  const [sending, setSending] = useState(false);
  const setF  = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setBF = k => e => setBcast(f => ({ ...f, [k]: e.target.value }));
  const TYPES = ['attendance','homework','fee','disciplinary','general','system'];

  const send = async (isBcast = false) => {
    setSending(true);
    try {
      const url = isBcast ? '/mobile/v1/admin/notify/parents' : '/mobile/v1/admin/notify';
      const payload = isBcast ? bcastForm : form;
      const r = await api.post(url, payload);
      enqueueSnackbar(isBcast ? `Sent to ${r.data.data?.sent} parent(s)` : 'Notification sent', { variant: 'success' });
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
    finally { setSending(false); }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Send fontSize="small" /> Send to Specific User
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <TextField select fullWidth size="small" label="User Type" value={form.user_type} onChange={setF('user_type')}>
                  <MenuItem value="parent">Parent</MenuItem>
                  <MenuItem value="teacher">Teacher</MenuItem>
                </TextField>
                <TextField fullWidth size="small" label="User ID" type="number" value={form.user_id} onChange={setF('user_id')} />
              </Stack>
              <TextField select fullWidth size="small" label="Type" value={form.type} onChange={setF('type')}>
                {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField fullWidth size="small" label="Title" value={form.title} onChange={setF('title')} />
              <TextField fullWidth size="small" label="Message" value={form.body} onChange={setF('body')} multiline rows={3} />
              <Button variant="contained" startIcon={<Send />} onClick={() => send(false)} disabled={sending || !form.user_id || !form.title} sx={{ bgcolor: '#0f2b4a' }}>
                {sending ? 'Sending…' : 'Send Notification'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <People fontSize="small" /> Broadcast to Student's Parents
            </Typography>
            <Stack spacing={2}>
              <TextField fullWidth size="small" label="Student ID" type="number" value={bcastForm.student_id} onChange={setBF('student_id')} />
              <TextField select fullWidth size="small" label="Type" value={bcastForm.type} onChange={setBF('type')}>
                {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField fullWidth size="small" label="Title" value={bcastForm.title} onChange={setBF('title')} />
              <TextField fullWidth size="small" label="Message" value={bcastForm.body} onChange={setBF('body')} multiline rows={3} />
              <Button variant="contained" color="secondary" startIcon={<Send />} onClick={() => send(true)} disabled={sending || !bcastForm.student_id || !bcastForm.title}>
                {sending ? 'Sending…' : 'Broadcast to Parents'}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
          <CardContent>
            <Typography fontWeight={700} gutterBottom>FCM / APNs Integration</Typography>
            <Alert severity="warning" sx={{ fontSize: 11 }}>
              <strong>Production setup required.</strong> To send real push notifications, integrate Firebase Cloud Messaging (Android) or APNs (iOS) in <code>mobileApiController.js</code> — the <code>sendPushNotification</code> function. All tokens are stored in <code>mobile_device_tokens</code>.
            </Alert>
            <Typography fontSize={11} color="text.secondary" sx={{ mt: 1 }}>
              Recommended: <strong>firebase-admin</strong> npm package for FCM. Use <code>admin.messaging().sendMulticast()</code> to send to multiple devices simultaneously.
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// ── Integration Guide Tab ──────────────────────────────────────────────────────
function GuideTab() {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const snippets = {
    flutter: `// Flutter (Dart) — parent app login + home screen
import 'dart:convert';
import 'package:http/http.dart' as http;

const BASE = '${base}';

Future<Map> parentLogin(String email, String password) async {
  final r = await http.post(
    Uri.parse('\\$BASE/parent/login'),
    headers: {'Content-Type': 'application/json'},
    body: jsonEncode({'email': email, 'password': password}),
  );
  return jsonDecode(r.body);
}

Future<Map> getHomeScreen(String token) async {
  final r = await http.get(
    Uri.parse('\\$BASE/mobile/v1/parent/home'),
    headers: {'Authorization': 'Bearer \\$token'},
  );
  return jsonDecode(r.body);
}

// Register device for push notifications
Future<void> registerDevice(String token, String deviceToken) async {
  await http.post(
    Uri.parse('\\$BASE/mobile/v1/device/register'),
    headers: {
      'Authorization': 'Bearer \\$token',
      'Content-Type': 'application/json'
    },
    body: jsonEncode({
      'device_token': deviceToken,
      'platform': 'android', // or 'ios'
      'app_version': '1.0.0'
    }),
  );
}`,

    reactNative: `// React Native — teacher app
import axios from 'axios';

const API = axios.create({ baseURL: '${base}' });

// Set auth header after login
const setToken = (token) => {
  API.defaults.headers.common['Authorization'] = \`Bearer \${token}\`;
};

// Teacher login
export const teacherLogin = async (username, password) => {
  const { data } = await API.post('/auth/login', { username, password });
  if (data.success) setToken(data.token);
  return data;
};

// Get home screen
export const getTeacherHome = () => API.get('/mobile/v1/teacher/home');

// Take attendance (works offline — collect, then sync)
export const saveAttendance = (classId, entries, date) =>
  API.post('/mobile/v1/teacher/attendance', {
    class_id: classId,
    date: date || new Date().toISOString().split('T')[0],
    entries // [{ student_id, status: 'present'|'absent'|'late'|'excused', notes }]
  });

// Save marks (bulk — safe to retry)
export const saveMarks = (assessmentId, entries) =>
  API.post('/mobile/v1/teacher/marks', {
    assessment_id: assessmentId,
    entries // [{ student_id, score }]
  });`,

    offlineSync: `// Offline sync pattern (React Native with AsyncStorage)
import AsyncStorage from '@react-native-async-storage/async-storage';

const QUEUE_KEY = 'offline_queue';

// Add to queue when offline
export const queueAction = async (action) => {
  const raw = await AsyncStorage.getItem(QUEUE_KEY) || '[]';
  const queue = JSON.parse(raw);
  queue.push({ ...action, timestamp: Date.now() });
  await AsyncStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
};

// Sync when back online
export const syncQueue = async () => {
  const raw = await AsyncStorage.getItem(QUEUE_KEY) || '[]';
  const queue = JSON.parse(raw);
  const failed = [];
  for (const item of queue) {
    try {
      if (item.type === 'attendance') {
        await saveAttendance(item.class_id, item.entries, item.date);
      } else if (item.type === 'marks') {
        await saveMarks(item.assessment_id, item.entries);
      }
    } catch (e) {
      failed.push(item); // keep for next sync
    }
  }
  await AsyncStorage.setItem(QUEUE_KEY, JSON.stringify(failed));
  return { synced: queue.length - failed.length, failed: failed.length };
};`
  };

  const [lang, setLang] = useState('flutter');

  return (
    <Box>
      <Alert severity="success" sx={{ mb: 3, fontSize: 12 }}>
        The Mobile API is production-ready for <strong>React Native</strong> and <strong>Flutter</strong>. All POST endpoints use upsert semantics (ON DUPLICATE KEY UPDATE) so they are safe to retry after network failures — the offline sync pattern below shows how to queue actions and sync when connectivity is restored.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { icon: <CheckCircle color="success" />, title: 'Offline-safe writes', desc: 'Attendance and marks use UPSERT — retry any time without creating duplicates' },
          { icon: <CheckCircle color="success" />, title: 'Low bandwidth', desc: 'Flat JSON responses — no deep nesting, only fields the app needs' },
          { icon: <CheckCircle color="success" />, title: 'Pagination', desc: 'All list endpoints support page + limit params with total in meta' },
          { icon: <Warning color="warning" />,     title: 'Push notifications', desc: 'FCM/APNs integration required in production — device tokens stored in DB' },
        ].map((f, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, textAlign: 'center' }}>
              {f.icon}
              <Typography fontSize={12} fontWeight={700} sx={{ mt: 0.5 }}>{f.title}</Typography>
              <Typography fontSize={10} color="text.secondary">{f.desc}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Code examples */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {[['flutter','Flutter (Dart)'],['reactNative','React Native'],['offlineSync','Offline Sync']].map(([k,label]) => (
          <Button key={k} size="small" variant={lang===k?'contained':'outlined'} onClick={() => setLang(k)}
            sx={{ bgcolor: lang===k?'#0f2b4a':undefined, fontSize: 11 }}>{label}</Button>
        ))}
      </Stack>

      <Box sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          <CopyButton text={snippets[lang]} />
        </Box>
        <Box sx={{ bgcolor: '#1e1e1e', color: '#d4d4d4', p: 2, borderRadius: 2, fontFamily: 'monospace', fontSize: 12, overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
          <pre style={{ margin: 0 }}>{snippets[lang]}</pre>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography fontWeight={700} gutterBottom>Recommended App Structure</Typography>
      <Grid container spacing={2}>
        {[
          { title: 'Parent App screens', items: ['Login / Register', 'Home (children cards)', 'Attendance calendar', 'Marks by subject', 'Fee statement & outstanding', 'Homework tracker', 'Disciplinary notices', 'Push notifications inbox'] },
          { title: 'Teacher App screens', items: ['Login', 'Home (today\'s summary)', 'Class list', 'Attendance register', 'Marks entry', 'Homework management', 'Prep Book', 'Notifications inbox'] },
        ].map(section => (
          <Grid item xs={12} sm={6} key={section.title}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography fontWeight={700} fontSize={13} gutterBottom>{section.title}</Typography>
              {section.items.map(item => (
                <Typography key={item} fontSize={12} sx={{ '&::before': { content: '"→ "', color: '#0f2b4a' } }}>{item}</Typography>
              ))}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function MobileApiDocs() {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneAndroid color="primary" /> Mobile App API
        </Typography>
        <Typography variant="body2" color="text.secondary">
          REST API for parent and teacher mobile apps · Push notifications · Flutter &amp; React Native ready
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_,v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<Api fontSize="small" />}            label="API Reference"    iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Notifications fontSize="small" />}  label="Push Notifications" iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Code fontSize="small" />}           label="Integration Guide" iconPosition="start" sx={{ fontSize: 12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}><DocsTab /></TabPanel>
      <TabPanel value={tab} index={1}><NotificationsTab /></TabPanel>
      <TabPanel value={tab} index={2}><GuideTab /></TabPanel>
    </Box>
  );
}
